import { cartCheckoutLog, summarizeCartLine } from '@/utils/cartCheckoutDebug';

export interface CartLine {
  id: string;
  stickerId: string;
  stickerTitle?: string;
  creatorId: string;
  creatorTitle: string;

  lineType: 'Ticket' | 'Glow' | 'Sweepstakes' | 'Donation' | 'Sticker';

  variantId?: string;
  variantTitle?: string;
  quantity?: number;
  unitCost?: number;
  isFree?: boolean;
  selectedOption?: string;

  addOnId?: string;

  sweepstakesEntries?: number;

  donationCauseId?: string;
  donationAmount?: number;

  sweepstakesEntryCost?: number;
  donationCauseIds?: string[];
  socialShopId?: string;
  glowId?: string;
  campaignType?: string;
}

export interface CartCreatorGroup {
  creatorId: string;
  creatorTitle: string;
  stickerId: string;
  lines: CartLine[];
}

interface CartState {
  lines: CartLine[];
}

const state: CartState = {
  lines: [],
};

const mutations = {
  ADD_LINE(s: CartState, line: CartLine) {
    s.lines.push(line);
  },
  REMOVE_LINE(s: CartState, lineId: string) {
    s.lines = s.lines.filter((l) => l.id !== lineId);
  },
  UPDATE_LINE(s: CartState, updated: CartLine) {
    const idx = s.lines.findIndex((l) => l.id === updated.id);
    if (idx > -1) s.lines.splice(idx, 1, updated);
  },
  CLEAR_CART(s: CartState) {
    s.lines = [];
  },
  CLEAR_STICKER_LINES(s: CartState, stickerId: string) {
    s.lines = s.lines.filter((l) => l.stickerId !== stickerId);
  },
  CLEAR_CREATOR_LINES(s: CartState, creatorId: string) {
    s.lines = s.lines.filter((l) => l.creatorId !== creatorId);
  },
};

function linesAreMergeable(existing: CartLine, incoming: Omit<CartLine, 'id'>): boolean {
  if (existing.stickerId !== incoming.stickerId) return false;
  if (existing.lineType !== incoming.lineType) return false;
  if (existing.creatorId !== incoming.creatorId) return false;

  if (incoming.lineType === 'Ticket') {
    return existing.variantId === incoming.variantId
      && (existing.selectedOption || undefined) === (incoming.selectedOption || undefined);
  }

  if (incoming.lineType === 'Sticker') {
    return existing.variantId === incoming.variantId
      && existing.campaignType === incoming.campaignType
      && existing.donationCauseId === incoming.donationCauseId
      && JSON.stringify(existing.donationCauseIds || [])
        === JSON.stringify(incoming.donationCauseIds || [])
      && existing.sweepstakesEntryCost === incoming.sweepstakesEntryCost
      && existing.socialShopId === incoming.socialShopId
      && existing.glowId === incoming.glowId
      && existing.unitCost === incoming.unitCost;
  }

  return false;
}

const getters = {
  cartLines: (s: CartState): CartLine[] => s.lines,

  creatorGroups: (s: CartState): CartCreatorGroup[] => {
    const groups: Record<string, CartCreatorGroup> = {};
    for (const line of s.lines) {
      if (!groups[line.creatorId]) {
        groups[line.creatorId] = {
          creatorId: line.creatorId,
          creatorTitle: line.creatorTitle,
          stickerId: line.stickerId,
          lines: [],
        };
      }
      groups[line.creatorId].lines.push(line);
    }
    return Object.values(groups);
  },

  lineCount: (s: CartState): number => s.lines.reduce(
    (sum, line) => sum + (line.quantity ?? 1),
    0,
  ),
  hasLines: (s: CartState): boolean => s.lines.length > 0,
};

const actions = {
  addLine({ commit, state }: any, line: Omit<CartLine, 'id'>) {
    const existing = state.lines.find((l: CartLine) => linesAreMergeable(l, line));
    if (existing) {
      const merged = {
        ...existing,
        quantity: (existing.quantity ?? 1) + (line.quantity ?? 1),
      };
      cartCheckoutLog('cart/addLine merged existing line', {
        existing: summarizeCartLine(existing),
        incoming: summarizeCartLine(line),
        merged: summarizeCartLine(merged),
      });
      commit('UPDATE_LINE', merged);
      return;
    }

    const id = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });
    const newLine = { ...line, id };
    cartCheckoutLog('cart/addLine new line', summarizeCartLine(newLine));
    commit('ADD_LINE', newLine);
  },
  removeLine({ commit }: any, lineId: string) {
    commit('REMOVE_LINE', lineId);
  },
  updateLine({ commit }: any, line: CartLine) {
    commit('UPDATE_LINE', line);
  },
  clearCart({ commit }: any) {
    commit('CLEAR_CART');
  },
  clearStickerLines({ commit }: any, stickerId: string) {
    commit('CLEAR_STICKER_LINES', stickerId);
  },
  clearCreatorLines({ commit }: any, creatorId: string) {
    commit('CLEAR_CREATOR_LINES', creatorId);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  getters,
  actions,
};
